import React, { useCallback, useEffect, useState } from "react";
import { format, startOfMonth } from "date-fns";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CalendarBlank, Funnel, DownloadSimple, EnvelopeSimple, Lock, SealCheck } from "@phosphor-icons/react";
import { toIsoLocal } from "@/components/payment/PaymentEntryGrid";

export const BLANK_TYPE = "__blank__";
const typeLabel = (t) => (t === BLANK_TYPE ? "(No type)" : t);

export function PaymentReportBar({ typeOptions, refreshKey, selectedIds = [], onClearSelection }) {
  const [range, setRange] = useState({ from: startOfMonth(new Date()), to: new Date() });
  const [types, setTypes] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  const params = useCallback(() => ({
    from: toIsoLocal(range.from), to: toIsoLocal(range.to || range.from),
    ...(types.length ? { types: types.join("||") } : {}),
    ...(selectedIds.length ? { ids: selectedIds.join(",") } : {}),
  }), [range, types, selectedIds]);

  useEffect(() => {
    api.get("/payment-requests/report/status", { params: params() })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, [params, refreshKey]);

  const canDl = !!status?.download?.ready;
  const canMail = !!status?.mail?.ready;
  const lockReason = (g) => (!status ? "Checking…"
    : status.count === 0 ? "No payments in this range / filter"
    : `None of these ${status.count} payment(s) are approved at L${g.level} yet`);

  const download = async () => {
    setBusy("dl");
    try {
      const p = params();
      const res = await api.get("/payment-requests/report.xlsx", { params: p, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `payments-${p.from}-to-${p.to}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error((await blobDetail(e)) || "Download failed");
    } finally { setBusy(null); }
  };

  const mail = async () => {
    if (!window.confirm(`Email ${status.mail.approved_count} L2-approved payment(s) (XLSX + summary) to:\n\n${(status?.recipients || []).join("\n")}`)) return;
    setBusy("mail");
    try {
      const p = params();
      const r = await api.post("/payment-requests/report/email", { from: p.from, to: p.to, types, ids: selectedIds });
      toast.success(`Report emailed to ${r.data.sent_to.length} recipients`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2"
      data-testid="pmt-report-bar">
      <span className="text-[9.5px] uppercase tracking-wider font-bold text-slate-500 mr-1">Approved report</span>
      {selectedIds.length ? (
        <span className="inline-flex items-center gap-2 px-2 py-1 rounded border border-cyan-700 bg-cyan-950/50 text-cyan-100 text-[11px]"
          data-testid="pmt-report-selection">
          {selectedIds.length} selected
          <button type="button" onClick={onClearSelection} className="text-cyan-300 hover:underline text-[10px]"
            data-testid="pmt-report-selection-clear">clear</button>
        </span>
      ) : (
        <>
          <RangePicker range={range} setRange={setRange} />
          <TypeFilter options={typeOptions} selected={types} setSelected={setTypes} />
        </>
      )}
      <StatusChip status={status} />
      <div className="flex-1" />
      <Button size="sm" variant="outline" onClick={download} disabled={!canDl || busy === "dl"}
        title={canDl ? `Download ${status.download.approved_count} L1-approved payment(s), grouped by cheque no.` : lockReason(status?.download)}
        className="h-8 border-emerald-800 text-emerald-300 bg-emerald-950/40 hover:bg-emerald-900/60 text-[11px] disabled:opacity-40"
        data-testid="pmt-report-download">
        {canDl ? <DownloadSimple size={12} weight="bold" className="mr-1" /> : <Lock size={12} weight="bold" className="mr-1" />}
        {busy === "dl" ? "Preparing…" : `Download XLS${canDl ? ` (${status.download.approved_count})` : ""}`}
      </Button>
      <Button size="sm" variant="outline" onClick={mail} disabled={!canMail || busy === "mail"}
        title={canMail ? `Email to ${(status?.recipients || []).join(", ")}` : lockReason(status?.mail)}
        className="h-8 border-cyan-800 text-cyan-300 bg-cyan-950/40 hover:bg-cyan-900/60 text-[11px] disabled:opacity-40"
        data-testid="pmt-report-mail">
        <EnvelopeSimple size={12} weight="bold" className="mr-1" />{busy === "mail" ? "Sending…" : `Mail${canMail ? ` (${status.mail.approved_count})` : ""}`}
      </Button>
    </div>
  );
}

async function blobDetail(e) {
  try { return JSON.parse(await e.response.data.text()).detail; } catch { return null; }
}

function RangePicker({ range, setRange }) {
  const [open, setOpen] = useState(false);
  const lbl = (d) => (d ? format(d, "dd MMM yy") : "…");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 bg-slate-950 border-slate-700 text-slate-100 font-mono text-[11px]"
          data-testid="pmt-report-range-trigger">
          <CalendarBlank size={12} weight="bold" className="mr-1.5 text-cyan-300" />
          {lbl(range.from)} → {lbl(range.to)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-slate-950 border-slate-700 text-slate-100 w-auto" align="start" data-testid="pmt-report-range-popover">
        <Calendar mode="range" numberOfMonths={2} selected={range} defaultMonth={range.from}
          onSelect={(r) => { if (r?.from) setRange({ from: r.from, to: r.to || r.from }); }} />
      </PopoverContent>
    </Popover>
  );
}

function TypeFilter({ options, selected, setSelected }) {
  const toggle = (t) => setSelected((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 bg-slate-950 border-slate-700 text-slate-100 text-[11px]"
          data-testid="pmt-report-filter-trigger">
          <Funnel size={12} weight="bold" className="mr-1.5 text-cyan-300" />
          {selected.length ? `${selected.length} type${selected.length > 1 ? "s" : ""}` : "All payment types"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0 bg-slate-950 border-slate-700 text-slate-100" align="start" data-testid="pmt-report-filter-popover">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 text-[10px]">
          <span className="uppercase tracking-wider font-bold text-slate-400">Payment type</span>
          <div className="flex gap-2">
            <button type="button" className="text-cyan-300 hover:underline" onClick={() => setSelected([...options])}
              data-testid="pmt-report-filter-all">All</button>
            <button type="button" className="text-slate-400 hover:underline" onClick={() => setSelected([])}
              data-testid="pmt-report-filter-clear">Clear</button>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {options.length === 0 && <div className="px-3 py-4 text-[11px] text-slate-500">No payment types yet</div>}
          {options.map((t) => (
            <label key={t} className="flex items-center gap-2 px-3 py-1.5 text-[11.5px] cursor-pointer hover:bg-slate-800"
              data-testid={`pmt-report-filter-opt-${t}`}>
              <input type="checkbox" checked={selected.includes(t)} onChange={() => toggle(t)} className="h-3.5 w-3.5 accent-cyan-400" />
              <span className="truncate">{typeLabel(t)}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StatusChip({ status }) {
  if (!status) return null;
  if (status.count === 0) {
    return <span className="px-2 py-1 rounded border border-slate-700 text-[10.5px] font-mono text-slate-400" data-testid="pmt-report-status">No payments in range</span>;
  }
  const pill = (g, label, tid) => (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[10.5px] font-mono ${g.ready ? "border-emerald-800 bg-emerald-950/50 text-emerald-200" : "border-amber-800 bg-amber-950/40 text-amber-200"}`}
      data-testid={tid} title={(g.pending_sample || []).map((p) => `${p.date} · ${p.vendor} · ₹${p.amount} · waiting: ${p.pending_role_label || "—"}`).join("\n")}>
      <SealCheck size={12} weight="fill" />{label} {g.approved_count}/{status.count}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="pmt-report-status">
      {pill(status.download, "L1 (download)", "pmt-report-status-l1")}
      {pill(status.mail, "L2 (mail)", "pmt-report-status-l2")}
      <span className="text-[10.5px] font-mono text-slate-400">{fmtINR(status.total_amount)}</span>
    </span>
  );
}
